/**
 * Webhook-outbox list (PR-6a — operational observability).
 *
 * Outbound webhook deliveries. State is derived from the projection
 * columns (delivered_at, dead_lettered_at, locked_until):
 *   - pending / in_flight / delivered / dead_lettered.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { RotateCw, Send, Webhook } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { NameAvatar } from "@/components/shell/NameAvatar";
import { ListPagination } from "@/components/shell/ListPagination";
import { useEnvironment } from "@/lib/environment-context";
import { isMissingPage, LIST_PAGE_SIZE, usePageParam } from "@/lib/use-page-param";
import { CopyId } from "@/components/shell/CopyId";
import { RelativeTime } from "@/components/shell/RelativeTime";
import { DataTable, type DataTableColumn } from "@/components/shell/DataTable";
import { EmptyState } from "@/components/shell/EmptyState";
import { FilterChip } from "@/components/shell/FilterChip";
import { PageHeader } from "@/components/shell/PageHeader";
import { SearchInput } from "@/components/shell/SearchInput";
import { Toolbar, FilterGroup } from "@/components/shell/Toolbar";
import {
  getWebhookOutbox,
  type WebhookOutboxRow,
  type WebhookOutboxState,
} from "@/lib/api";
import { useAuth } from "@/lib/use-auth";

const STATES: readonly WebhookOutboxState[] = [
  "pending",
  "in_flight",
  "delivered",
  "dead_lettered",
] as const;

const STATE_VARIANT: Record<WebhookOutboxState, BadgeProps["variant"]> = {
  pending: "muted",
  in_flight: "info",
  delivered: "success",
  dead_lettered: "destructive",
};

export function WebhookOutboxListPage() {
  const [params, setParams] = useSearchParams();
  const { page, setPage } = usePageParam();
  const { environment: env } = useEnvironment();
  const navigate = useNavigate();
  const { user } = useAuth();

  const state = (params.get("state") as WebhookOutboxState | null) ?? undefined;
  const event_type = params.get("event_type") ?? undefined;
  const q = params.get("q") ?? undefined;

  const [searchInput, setSearchInput] = useState(q ?? "");
  useEffect(() => {
    const t = setTimeout(() => {
      // Also skips the mount run, so a shared ?page=3 link keeps its page.
      if (searchInput === (params.get("q") ?? "")) return;
      const next = new URLSearchParams(params);
      if (searchInput) next.set("q", searchInput);
      else next.delete("q");
      next.delete("page");
      setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["webhook-outbox", { env, state, event_type, q, page }] as const,
    queryFn: () => getWebhookOutbox({ environment: env, state, event_type, q, page, page_size: LIST_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.error && !isMissingPage(query.error, page)) {
      toast.error("Couldn't load webhook outbox", {
        description: "Check your network and try again.",
      });
    }
  }, [query.error, page]);

  function setFilter(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    next.delete("page");
    setParams(next, { replace: true });
  }

  const hasFilters = Boolean(state || event_type || q);
  const isZeroGrantTenant =
    user?.role === "tenant" && user.tenants.length === 0 && !hasFilters;

  const columns = useMemo<ReadonlyArray<DataTableColumn<WebhookOutboxRow>>>(
    () => [
      {
        key: "id",
        header: "Outbox row",
        cell: (r) => (
          <CopyId id={r.id} />
        ),
      },
      {
        key: "tenant",
        header: "Tenant",
        cell: (r) => (
          <div className="flex min-w-0 items-center gap-2">
            <NameAvatar name={r.tenant.name} />
            <span className="truncate font-medium">{r.tenant.name}</span>
            {r.tenant.environment === "test" ? <Badge variant="muted">Test</Badge> : null}
          </div>
        ),
      },
      {
        key: "event_type",
        header: "Event",
        cell: (r) => <span className="font-mono text-xs">{r.event_type}</span>,
      },
      {
        key: "state",
        header: "State",
        cell: (r) => (
          <Badge dot variant={STATE_VARIANT[r.state]} className="capitalize">
            {r.state.replace(/_/g, " ")}
          </Badge>
        ),
      },
      {
        key: "attempts",
        header: "Attempts",
        className: "text-right tabular-nums",
        cell: (r) => <span className="text-sm">{r.attempts}</span>,
      },
      {
        key: "created_at",
        header: "Enqueued",
        className: "text-muted-foreground",
        cell: (r) => (
          <RelativeTime iso={r.created_at} />
        ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        icon={Webhook}
        title="Webhook outbox"
        description="Outbound webhook deliveries. Click a row to see every attempt against the destination."
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RotateCw className={query.isFetching ? "animate-spin" : undefined} />
            Refresh
          </Button>
        }
      />

      <Toolbar
        search={
          <SearchInput
            placeholder="Search by dedup key…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onClear={() => setSearchInput("")}
          />
        }
        filters={
          <>
            <FilterGroup label="State">
              <FilterChip
                active={state === undefined}
                onClick={() => setFilter("state", null)}
              >
                All
              </FilterChip>
              {STATES.map((s) => (
                <FilterChip
                  key={s}
                  active={state === s}
                  onClick={() => setFilter("state", s)}
                >
                  {s.replace(/_/g, " ")}
                </FilterChip>
              ))}
            </FilterGroup>
          </>
        }
      />

      <DataTable<WebhookOutboxRow>
        stale={query.isPlaceholderData}
        columns={columns}
        rows={query.data?.results ?? []}
        loading={query.isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => navigate(`/webhook-outbox/${r.id}`)}
        emptyState={
          isZeroGrantTenant ? (
            <EmptyState
              icon={Send}
              title="No tenants granted yet"
              description="Outbox rows appear here once a super-admin grants you access to a tenant."
            />
          ) : (
            <EmptyState
              icon={Send}
              title="No outbox rows match these filters"
              description="Try clearing a filter. Outbox rows are enqueued as your platform emits webhooks."
              action={
                hasFilters ? (
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/webhook-outbox">Clear filters</Link>
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />

      <ListPagination query={query} page={page} onPage={setPage} />
    </>
  );
}

export default WebhookOutboxListPage;
